import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Player } from '@/lib/gameStore';
import { Button } from './ui/button';
import { useSound } from '@/hooks/useSound';

interface VoiceActivationProps {
  roomId: string;
  playerId: string;
  players: Player[];
  onContinue: () => void;
  onWin: () => void;
}

export default function VoiceActivation({ 
  roomId, 
  playerId, 
  players, 
  onContinue, 
  onWin 
}: VoiceActivationProps) {
  const [activated, setActivated] = useState<Set<string>>(new Set());
  const [hasActivated, setHasActivated] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(2);
  const { playSound } = useSound();

  // Play alert sound on mount
  useEffect(() => {
    playSound('alert');
  }, [playSound]);

  useEffect(() => {
    // Subscribe to activations
    const channel = supabase
      .channel(`voice-activation-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'players',
          filter: `room_id=eq.${roomId}`
        },
        (payload) => {
          const player = payload.new as any;
          if (player.socket_id === 'activated') {
            setActivated(prev => new Set([...prev, player.id]));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1 && prev > 0.9) {
          playSound('countdown');
        }
        if (prev <= 0.1) {
          checkWinCondition();
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [activated.size, playSound]);

  const handleActivate = async () => {
    if (hasActivated) return;

    playSound('activate');
    setHasActivated(true);
    setActivated(prev => new Set([...prev, playerId]));

    // Mark as activated
    await supabase
      .from('players')
      .update({ socket_id: 'activated' })
      .eq('id', playerId);
  };

  const checkWinCondition = async () => {
    const activatedCount = activated.size;
    const totalPlayers = players.filter(p => p.isActive).length;
    const threshold = Math.ceil(totalPlayers * 0.75);

    if (activatedCount >= threshold) {
      onWin();
    } else {
      // Reset and continue
      await supabase
        .from('players')
        .update({ socket_id: null })
        .eq('room_id', roomId);
      
      onContinue();
    }
  };

  const activatedCount = activated.size;
  const totalPlayers = players.filter(p => p.isActive).length;

  return (
    <motion.div
      className="fixed inset-0 bg-chaos-green/30 backdrop-blur-md z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="text-center space-y-8">
        {/* Main message */}
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 0.5, 
            repeat: Infinity 
          }}
          className="space-y-4"
        >
          <div className="text-9xl font-black text-chaos-green text-shadow glow-green">
            67!
          </div>
          <div className="text-4xl font-black text-foreground">
            SHOUT "SIX-SEVEN"!
          </div>
        </motion.div>

        {/* Activate button */}
        <Button
          onClick={handleActivate}
          disabled={hasActivated}
          size="lg"
          className="text-4xl px-16 py-8 h-auto font-black rounded-3xl glow-green"
        >
          {hasActivated ? '✓ ACTIVATED' : 'ACTIVATE'}
        </Button>

        {/* Counter */}
        <div className="glass-card px-8 py-6 rounded-3xl inline-block">
          <div className="text-2xl font-bold text-muted-foreground mb-2">
            ACTIVATED
          </div>
          <div className="text-6xl font-black text-chaos-green">
            {activatedCount}/{totalPlayers}
          </div>
        </div>

        {/* Timer */}
        <motion.div
          className="text-6xl font-black text-foreground"
          animate={{ 
            scale: timeRemaining < 1 ? [1, 1.2, 1] : 1,
            color: timeRemaining < 1 ? ['#fff', '#ff0000', '#fff'] : '#fff'
          }}
          transition={{ duration: 0.5, repeat: timeRemaining < 1 ? Infinity : 0 }}
        >
          {timeRemaining.toFixed(1)}s
        </motion.div>

        <div className="text-xl text-muted-foreground font-bold">
          Need {Math.ceil(totalPlayers * 0.75)} players to win!
        </div>
      </div>
    </motion.div>
  );
}
